/**
 * ThemeToggle.tsx — Sprint 24.4
 *
 * Toggles between dark (default) and light themes by toggling the
 * `data-theme="light"` attribute on <html>. CSS vars for the light
 * theme are defined in index.css under [data-theme="light"].
 *
 * Preference is persisted to localStorage under key `admin_theme`.
 */

import { useEffect, useState } from 'react';

const THEME_KEY = 'admin_theme';

function applyTheme(theme: 'dark' | 'light') {
  if (theme === 'light') {
    document.documentElement.setAttribute('data-theme', 'light');
  } else {
    document.documentElement.removeAttribute('data-theme');
  }
}

function getInitialTheme(): 'dark' | 'light' {
  const stored = localStorage.getItem(THEME_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return 'dark';
}

export default function ThemeToggle() {
  const [theme, setTheme] = useState<'dark' | 'light'>(getInitialTheme);

  // Apply on mount and whenever theme changes
  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  function toggle() {
    setTheme(t => t === 'dark' ? 'light' : 'dark');
  }

  return (
    <button
      className="btn btn-ghost btn-sm theme-toggle"
      onClick={toggle}
      title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
    >
      {theme === 'dark' ? '☀' : '◑'}
    </button>
  );
}
